import { supabase } from "@/integrations/supabase/client";
import { nestSignOut } from "@/services/api";
import {
  clearNestAccessToken,
  clearNestRefreshToken,
  readNestRefreshToken,
} from "@/lib/authHelpers";
import { captureSessionLost } from "@/lib/sentry";
import { BOOT_RACE_WINDOW_MS } from "@/lib/nestFallback";

/**
 * Central self-heal for the "dead session query storm" class (#3443 / the
 * 2026-07-22 17:28 burst): a tab whose token expired unrecoverably keeps its
 * cached companyId, so every mounted hook still fires — Nest calls 401/auth-fail
 * into per-endpoint `nest-fallback` events, and the Supabase fallbacks execute
 * as `anon`, raising 42501 per query (`rpc-permission-denial` + table-grant
 * denials). The user is left on a permanently broken dashboard, and one stale
 * tab files a dozen Sentry groups in a minute.
 *
 * The discriminator between that state and a GENUINE authorization denial is
 * the session itself: a live `getSession()` means a real permission problem
 * (kept fully observable — a wrongful-denial regression must reach Sentry);
 * a null session past the boot window means the errors are artifacts of auth
 * loss, and the fix is to finish the sign-out the auth layer never completed —
 * clear the cached Nest tokens and sign out locally, which routes the user to
 * /auth via the existing onAuthStateChange → ProtectedRoute path.
 *
 * The heal is reported through `captureSessionLost` (deduped, its own
 * fingerprint) so involuntary logouts stay countable as ONE signal instead of
 * N per-query noise events. Suppression never happens silently.
 */

/**
 * One heal ACTION per window: a storm delivers every query's error in a burst,
 * and a single sign-out heals all of them. The cooldown gates only the heal
 * side effects (capture + sign-out) — dead-vs-live classification below always
 * re-checks the real session, so a genuine denial after a fast re-login is
 * never mistaken for a storm remnant.
 */
const HEAL_COOLDOWN_MS = 30_000;
let lastHealFiredAt = 0;

/** Test-only reset for the module-level cooldown. */
export function resetSessionHealForTests(): void {
  lastHealFiredAt = 0;
}

/**
 * Returns true when the error context is a dead session (no live Supabase
 * session) — the caller should SKIP its per-query Sentry capture, because the
 * heal path owns the (single, deduped) signal. Returns false when a session
 * is live, i.e. the error is a genuine denial the caller must report.
 *
 * Never rejects: on an unexpected getSession/sign-out failure the session
 * state is unknown, and the safe answer is false — keep reporting rather than
 * suppress — so call sites may fire-and-forget (`void healIfSessionDead(…)`).
 *
 * `where` labels the call site in the heal telemetry (e.g. "query-cache").
 */
export async function healIfSessionDead(where: string): Promise<boolean> {
  // During app boot, queries legitimately race auth hydration and self-heal —
  // signing the user out for a boot race would turn a cosmetic race into a
  // real logout. Same window the fallback reporter uses.
  if (performance.now() < BOOT_RACE_WINDOW_MS) return false;

  try {
    const { data } = await supabase.auth.getSession();
    if (data.session) return false;

    // No cached Nest refresh token → this is the tail of a VOLUNTARY sign-out
    // (signOut() clears the tokens before revoking, so in-flight queries error
    // after the tokens are gone). The redirect is already underway — suppress
    // the storm remnants without reporting a phantom "forced signout".
    // (gotrue-mode caveat: no Nest tokens are ever cached there, so this makes
    // the heal suppression-only — acceptable, gotrue is being decommissioned.)
    if (!readNestRefreshToken()) return true;

    // Cooldown check-and-set with no await in between: concurrent storm calls
    // interleave only at the getSession await above, so exactly one caller can
    // pass this gate per window — the rest classify as remnants.
    const now = Date.now();
    if (now - lastHealFiredAt < HEAL_COOLDOWN_MS) return true;
    lastHealFiredAt = now;

    clearNestRefreshToken();
    clearNestAccessToken();

    // Telemetry failure must never block the heal itself — the sign-out below
    // still runs if the Sentry SDK throws.
    try {
      captureSessionLost({
        reason: "dead_session_query_storm",
        where,
        route: window.location.pathname,
      });
    } catch {
      /* heal proceeds without the signal */
    }

    await nestSignOut({ scope: "local" });

    return true;
  } catch {
    return false;
  }
}
